import type { ParticipantInvitationPublicView } from "@closer/shared";
import { Button, Card, Spinner, useToast } from "@closer/ui";
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";

import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";

/**
 * Haricî katılımcı davet ekranı (CLOS-147 `[128]`).
 *
 * Davet e-postası `/katilimci-davet?token=...` adresine yönlendiriyor; `[126]`/`[127]`
 * uçları hazırdı ama rota yoktu — organizasyon davetinde bir kez yaşanan "yeşil uçlar,
 * kopuk halka" dersi (CLOS-74) burada baştan kapatılıyor.
 *
 * Oturum GEREKTİRMİYOR (InvitationPage ile aynı gerekçe): davetli kime ve neye
 * çağrıldığını kaydolmadan önce görebilmeli. Kabul/ret kararı SUNUCUDA; e-posta
 * uyuşmazlığı burada yalnız hatırlatma.
 */

type Durum = "yukleniyor" | "hazir" | "gecersiz" | "islemde" | "reddedildi";

export function ParticipantInvitationPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const toast = useToast();
  const { user, logout } = useAuth();

  const [davet, setDavet] = useState<ParticipantInvitationPublicView | null>(null);
  const [durum, setDurum] = useState<Durum>(token ? "yukleniyor" : "gecersiz");
  const [hata, setHata] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    void (async () => {
      try {
        const { data } = await api<ParticipantInvitationPublicView>(
          `/api/participant-invitations?token=${encodeURIComponent(token)}`,
        );
        setDavet(data);
        setDurum("hazir");
      } catch {
        // Var olmayan/dolmuş/iptal edilmiş davet sunucuda aynı cevaba düşüyor; ekran da
        // ayırt ediyormuş gibi davranmıyor — atılacak adım hepsinde aynı.
        setDurum("gecersiz");
      }
    })();
  }, [token]);

  async function yanitla(karar: "accept" | "reject") {
    if (!token) return;
    setDurum("islemde");
    setHata(null);
    try {
      await api(`/api/participant-invitations/${karar}`, {
        method: "POST",
        body: JSON.stringify({ token }),
      });
      if (karar === "reject") {
        setDurum("reddedildi");
        return;
      }
      toast.success("Davet kabul edildi");
      /** Erişim kabulde doğdu; haricî çalışma görünümü artık bu kullanıcıya açık. */
      navigate("/harici");
    } catch (error) {
      setHata(
        error instanceof ApiRequestError
          ? error.message
          : "Davet yanıtlanamadı. Lütfen tekrar deneyin.",
      );
      setDurum("hazir");
    }
  }

  const hesapDegistir = async (): Promise<void> => {
    try {
      await logout();
      navigate(`/login?katilimci=${encodeURIComponent(token ?? "")}`);
    } catch {
      toast.error("Çıkış yapılamadı. Lütfen tekrar deneyin.");
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas p-4">
      <Card title="Sözleşme Daveti" className="w-full max-w-sm">
        {durum === "yukleniyor" && (
          <div role="status" className="flex flex-col items-center gap-3 py-4">
            <Spinner size="lg" />
            <p className="text-sm text-fg-muted">Davet kontrol ediliyor…</p>
          </div>
        )}

        {durum === "gecersiz" && (
          <div className="flex flex-col gap-4">
            <p role="status" className="text-sm text-fg-muted">
              Bu davet geçerli değil. Bağlantının süresi dolmuş, davet iptal edilmiş ya da daha önce
              yanıtlanmış olabilir. Sizi davet eden kişiden yeni bir davet isteyebilirsiniz.
            </p>
            <Link to="/login" className="text-center font-medium text-primary hover:underline">
              Giriş sayfasına dön
            </Link>
          </div>
        )}

        {durum === "reddedildi" && davet && (
          <div className="flex flex-col gap-4">
            <p role="status" className="text-sm text-fg-muted">
              Daveti reddettiniz. <strong>{davet.contractTitle}</strong> sözleşmesine erişim
              almadınız.
            </p>
            <Link to="/login" className="text-center font-medium text-primary hover:underline">
              Giriş sayfasına dön
            </Link>
          </div>
        )}

        {(durum === "hazir" || durum === "islemde") && davet && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <p className="text-sm text-fg-muted">
                <strong className="text-fg">{davet.organizationName}</strong>, sizi{" "}
                <strong className="text-fg">{davet.contractTitle}</strong> sözleşmesine haricî
                katılımcı olarak davet etti.
              </p>
              <p className="text-sm text-fg-soft">
                Erişim: Görüntüleme
                {davet.accessEndsAt
                  ? ` · ${new Date(davet.accessEndsAt).toLocaleDateString("tr-TR")} tarihine kadar`
                  : " · kaldırılana kadar"}
              </p>
            </div>

            {!user?.email ? (
              <>
                <p className="text-sm text-fg-muted">
                  Daveti yanıtlamak için <strong>{davet.email}</strong> adresiyle giriş yapın. Bu
                  adresle hesabınız yoksa önce kayıt olun; işlem bitince bu sayfaya geri dönersiniz.
                </p>
                <Link
                  to={`/login?katilimci=${encodeURIComponent(token ?? "")}`}
                  className="text-center font-medium text-primary hover:underline"
                >
                  Giriş yap
                </Link>
                <Link
                  to={`/register?katilimci=${encodeURIComponent(token ?? "")}`}
                  className="text-center font-medium text-primary hover:underline"
                >
                  Kayıt ol
                </Link>
              </>
            ) : (
              <>
                <p role="status" className="text-sm text-fg-muted">
                  Bu davet <strong>{davet.email}</strong> adresine gönderildi; yanıtlamak için o
                  hesapla giriş yapmış olmalısınız. Şu an <strong>{user.email}</strong> ile
                  giriştesiniz.
                </p>

                {hata && (
                  <p role="alert" className="text-sm text-danger">
                    {hata}
                  </p>
                )}

                <Button onClick={() => void yanitla("accept")} loading={durum === "islemde"}>
                  Daveti kabul et
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => void yanitla("reject")}
                  disabled={durum === "islemde"}
                >
                  Reddet
                </Button>

                <p className="text-center text-sm text-fg-soft">
                  Bu hesap değil mi?{" "}
                  <Button
                    variant="ghost"
                    onClick={() => void hesapDegistir()}
                    disabled={durum === "islemde"}
                  >
                    Farklı hesapla giriş yap
                  </Button>
                </p>
              </>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
